import React,{useEffect,useRef,useState} from 'react'
import { CheckCircle2,Cloud,Disc3,HardDrive,Headphones,Loader2,Music2,Pause,Play,Plus,ShieldCheck,Trash2,UploadCloud } from 'lucide-react'
import {useAuth} from '../contexts/AuthContext'
import {useContext} from 'react'
import {FamilyContext} from '../contexts/FamilyContext'
import {requestDriveAccess,findOrCreateFolder,listMusic,uploadMusic,downloadMusic,deleteMusic} from '../services/personalMusicDrive'

const clientId=import.meta.env.VITE_GOOGLE_CLIENT_ID
const card='rounded-2xl border border-[#e3e5dc] bg-white p-5 shadow-[0_9px_35px_rgba(19,59,47,0.06)] sm:p-7'
export default function FavoriteSongs(){
 const {user}=useAuth()
 const {family}=useContext(FamilyContext)
 const [token,setToken]=useState(null)
 const [folder,setFolder]=useState(null)
 const [songs,setSongs]=useState([])
 const [current,setCurrent]=useState(null)
 const [source,setSource]=useState(null)
 const [busy,setBusy]=useState(false)
 const [error,setError]=useState('')
 const [message,setMessage]=useState('')
 const [search,setSearch]=useState('')
 const [playing,setPlaying]=useState(false)
 const audio=useRef(null)
 const currentUrl=useRef(null)
 const fileInput=useRef(null)

 const clearPlayback=()=>{
  if(audio.current){audio.current.pause();audio.current.removeAttribute('src');audio.current.load()}
  if(currentUrl.current){URL.revokeObjectURL(currentUrl.current);currentUrl.current=null}
  setSource(null);setCurrent(null);setPlaying(false)
 }
 useEffect(()=>()=>{if(currentUrl.current)URL.revokeObjectURL(currentUrl.current)},[])
 useEffect(()=>{setToken(null);setFolder(null);setSongs([]);clearPlayback();setError('');setMessage('')},[user?.id])

 const refresh=async(access=token,folderId=folder)=>{
  if(!access||!folderId)return
  setSongs(await listMusic(access,folderId))
 }

 const run=async task=>{
  setBusy(true);setError('');setMessage('')
  try{await task()}catch(e){setError(e.message||'Não foi possível concluir a operação.')}
  finally{setBusy(false)}
 }

 const connect=()=>run(async()=>{
  const access=await requestDriveAccess(clientId)
  const folderId=await findOrCreateFolder(access)
  const items=await listMusic(access,folderId)
  clearPlayback()
  setToken(access);setFolder(folderId);setSongs(items)
  setMessage('Google Drive pessoal ligado. A coletânea pertence à conta Google autorizada.')
 })

 const addFiles=event=>{
  const files=Array.from(event.target.files||[])
  event.target.value=''
  if(!files.length)return
  run(async()=>{
   let uploaded=0
   for(const file of files){await uploadMusic(token,folder,file);uploaded++}
   await refresh()
   setMessage(uploaded===1?'Música guardada no seu Drive.':uploaded+' músicas guardadas no seu Drive.')
  })
 }

 const selectSong=song=>run(async()=>{
  clearPlayback()
  const blob=await downloadMusic(token,song.id)
  const url=URL.createObjectURL(blob)
  currentUrl.current=url
  setCurrent(song);setSource(url)
  setMessage('Música carregada do seu Google Drive.')
 })

 const remove=song=>{
  if(!window.confirm('Eliminar "'+song.name+'" do seu Google Drive?'))return
  run(async()=>{
   if(current?.id===song.id)clearPlayback()
   await deleteMusic(token,song.id);await refresh()
   setMessage('Música eliminada do seu Drive.')
  })
 }
 const visible=songs.filter(s=>s.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()))
 return <div className="mx-auto max-w-6xl space-y-7 pb-10">
  <div className="relative overflow-hidden rounded-3xl bg-[#153b30] px-7 py-10 text-white shadow-[0_18px_50px_rgba(19,59,47,.16)] sm:px-10">
   <div className="pointer-events-none absolute -right-10 -top-24 h-72 w-72 rounded-full border border-[#d7c598]/20"/>
   <div className="pointer-events-none absolute right-8 top-5 h-52 w-52 rounded-full border border-[#d7c598]/20"/>
   <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[.19em] text-[#e2cea0]"><Headphones size={16}/> O meu espaço musical</span>
   <h1 className="relative mt-4 text-3xl font-semibold tracking-tight sm:text-5xl">Minha Canção Preferida</h1>
   <p className="relative mt-4 max-w-2xl text-sm leading-7 text-emerald-50/80 sm:text-base">As músicas que contam a sua história. Uma coletânea pessoal, no seu próprio Google Drive, ao seu ritmo.</p>
   <div className="relative mt-7 flex flex-wrap items-center gap-3 text-xs">
    <span className="rounded-full border border-white/20 bg-white/10 px-3 py-2">{user?.displayName||user?.email||'Conta pessoal'}</span>
    <span className="rounded-full border border-white/20 bg-white/10 px-3 py-2">{family?.name||'Espaço individual'}</span>
   </div>
  </div>
  <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_310px]">
   <section className={card}>
    <div className="flex flex-wrap items-start justify-between gap-4">
     <div><h2 className="flex items-center gap-2 text-xl font-semibold text-[#183b31]"><Music2 size={23}/> A minha coletânea</h2>
      <p className="mt-2 text-sm leading-6 text-stone-600">MP3 pessoais, organizados dentro da pasta DOMUS do seu Drive.</p></div>
     {token&&<span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800"><CheckCircle2 size={15}/> Drive ligado</span>}
    </div>
    {error&&<div role="alert" className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>}
    {message&&<div role="status" className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">{message}</div>}
    {!clientId?<div className="mt-7 rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm leading-7 text-amber-900">A ligação ao Google Drive ainda precisa da configuração <code>VITE_GOOGLE_CLIENT_ID</code> no Coolify. A biblioteca só guardará músicas no Drive após essa configuração.</div>:
    !token?<div className="mt-8 flex flex-col items-center rounded-2xl border border-dashed border-[#bdd1c5] bg-[#f5f8f3] px-6 py-12 text-center">
      <span className="rounded-2xl bg-[#e3ece3] p-5 text-emerald-900"><HardDrive size={38}/></span>
      <h3 className="mt-5 text-xl font-semibold text-[#153b30]">A sua música, no seu Drive</h3>
      <p className="mt-3 max-w-md text-sm leading-7 text-stone-600">Ligue a sua conta Google pessoal para criar uma pasta privada de músicas. O DOMUS só solicita acesso aos ficheiros que criar por esta integração.</p>
      <button onClick={connect} disabled={busy} className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#176647] px-6 py-3.5 font-semibold text-white hover:bg-[#115437] disabled:opacity-50">{busy?<Loader2 className="animate-spin" size={18}/>:<Cloud size={18}/>} Ligar o meu Google Drive</button>
     </div>:
     <div className="mt-7">
      <div className="flex flex-wrap gap-3">
       <input ref={fileInput} type="file" accept=".mp3,audio/mpeg" multiple onChange={addFiles} className="hidden" aria-label="Selecionar músicas MP3"/>
       <button onClick={()=>fileInput.current?.click()} disabled={busy} className="inline-flex items-center gap-2 rounded-xl bg-[#176647] px-5 py-3 font-semibold text-white hover:bg-[#115437] disabled:opacity-50"><Plus size={18}/> Adicionar MP3</button>
       <button onClick={()=>run(()=>refresh())} disabled={busy} className="inline-flex items-center gap-2 rounded-xl border border-stone-300 bg-white px-5 py-3 font-semibold text-emerald-900 hover:bg-stone-50 disabled:opacity-50"><Cloud size={18}/> Atualizar</button>
      </div>
      {busy&&<p role="status" className="mt-4 flex items-center gap-2 text-sm text-emerald-800"><Loader2 size={17} className="animate-spin"/> A processar ficheiros no Google Drive...</p>}
      <div className="mt-7 flex flex-wrap items-center justify-between gap-3"><p className="text-sm font-semibold text-stone-700">{songs.length} {songs.length===1?'música':'músicas'} no meu Drive</p><input type="search" value={search} onChange={e=>setSearch(e.target.value)} placeholder="Pesquisar músicas..." aria-label="Pesquisar músicas" className="w-full rounded-xl border border-stone-200 px-4 py-2.5 text-sm outline-none focus:border-emerald-700 sm:w-60"/></div>
      {!visible.length?<div className="mt-6 rounded-2xl border border-dashed border-stone-200 bg-stone-50 p-10 text-center"><Disc3 className="mx-auto text-emerald-800" size={37}/><p className="mt-3 font-semibold text-stone-800">{songs.length?'Nenhuma música encontrada':'A sua coletânea começa aqui'}</p><p className="mt-2 text-sm text-stone-500">{songs.length?'Experimente outra pesquisa.':'Adicione um MP3 para guardar a sua primeira canção.'}</p></div>:
      <ul className="mt-5 space-y-3">{visible.map((song,index)=><li key={song.id} className="flex items-center gap-3 rounded-xl border border-stone-200 bg-[#fbfcfa] px-4 py-3 shadow-sm">
       <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#e7ede6] text-emerald-900"><Music2 size={21}/></span>
       <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold text-stone-900" title={song.name}>{song.name.replace(/\.mp3$/i,'')}</p><p className="text-xs text-stone-500">{song.size?(Number(song.size)/1048576).toFixed(1)+' MB · ':''}MP3 · Google Drive</p></div>
       <button onClick={()=>selectSong(song)} disabled={busy} title={'Reproduzir '+song.name} aria-label={'Reproduzir '+song.name} className="rounded-lg bg-[#176647] p-2.5 text-white hover:bg-[#115437] disabled:opacity-50"><Play size={17}/></button>
       <button onClick={()=>remove(song)} disabled={busy} title={'Eliminar '+song.name} aria-label={'Eliminar '+song.name} className="rounded-lg p-2.5 text-stone-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"><Trash2 size={17}/></button>
      </li>)}</ul>}
     </div>}
   </section>
   <aside className="space-y-6">
    <div className={card}><h2 className="flex items-center gap-2 text-lg font-semibold text-[#183b31]"><Headphones size={21}/> A ouvir agora</h2>
     <div className="mt-5 flex h-40 items-center justify-center rounded-2xl bg-[#173c32] text-[#e5cf9b]"><Disc3 size={72} strokeWidth={1.1} className={playing?'animate-spin':''}/></div>
     <p className="mt-4 truncate text-center font-semibold text-stone-900">{current?current.name.replace(/\.mp3$/i,''):'Escolha uma canção'}</p>
     <p className="mt-1 text-center text-xs text-stone-500">Reprodutor pessoal</p>
     <audio ref={audio} src={source||undefined} controls={Boolean(source)} onPlay={()=>setPlaying(true)} onPause={()=>setPlaying(false)} onEnded={()=>setPlaying(false)} className="mt-5 w-full" preload="none"/>
    </div>
    <div className={card}><h3 className="flex items-center gap-2 font-semibold text-[#183b31]"><ShieldCheck size={20}/> Biblioteca pessoal</h3>
     <p className="mt-3 text-sm leading-7 text-stone-600">Os MP3 são guardados na conta Google autorizada, não no PostgreSQL familiar. Outros membros não recebem acesso automático à sua coletânea.</p>
     <p className="mt-3 text-xs leading-6 text-stone-500">Requer ligação ao Google Drive e permissão concedida. Utilize apenas músicas que tem direito de armazenar. O áudio é carregado para reprodução quando selecionar uma faixa.</p>
    </div>
   </aside>
  </div>
 </div>
}
